import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Check, ChevronRight, Play, X as XMark, Star } from 'lucide-react';
import SEO from '@/components/SEO';
import Icon from '@/components/Icon';
import BrandIcon from '@/components/BrandIcon';
import {
  site,
  products,
  features,
  channels,
  faqs,
  industries,
  customerStats,
  customerLogos,
  calculator,
  migration,
  comparison,
} from '@/data/content';

export default function Home() {
  const [agents, setAgents] = useState(10);
  const maxPrice = Math.max(...calculator.competitors.map((c) => c.price), calculator.zuriPrice);
  const fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  const productIcons: Record<string, string> = { 'zyra-ai': 'Sparkles', 'zuri-cloud': 'Cloud', 'zuri-voice': 'Phone' };

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: site.name,
      url: site.baseUrl,
      logo: `${site.baseUrl}/logo.png`,
      sameAs: [`https://twitter.com/${site.twitter}`],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: site.name,
      url: site.baseUrl,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${site.baseUrl}/?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: site.name,
      applicationCategory: 'BusinessApplication',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
      description: site.description,
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.8',
        ratingCount: '120',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.answer,
        },
      })),
    },
  ];

  function compareCell(value: boolean | string, highlight = false) {
    if (typeof value === 'string') {
      const isPrice = value.startsWith('$');
      return <span className={`${isPrice ? 'font-bold' : 'font-medium'} ${highlight ? 'text-brand-700' : 'text-slate-700'}`}>{value}</span>;
    }
    if (value === true) return <Check className="mx-auto h-5 w-5 text-green-600" />;
    if (value === false) return <XMark className="mx-auto h-5 w-5 text-slate-300" />;
    return null;
  }

  function badge(value: string) {
    if (value === 'partial') return <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-700">Partial</span>;
    if (value === 'add-on') return <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-bold text-slate-700">Add-on</span>;
    return null;
  }

  return (
    <>
      <SEO title="Home" description={site.description} path="/" jsonLd={jsonLd}>
        <meta name="keywords" content="customer experience, CX software, live chat, support tickets, knowledge base, omnichannel, AI voice, Zuri Hub" />
      </SEO>

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-24 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-brand-600/20 blur-3xl" />
        <div className="absolute top-1/2 -left-24 h-64 w-64 rounded-full bg-indigo-500/10 blur-3xl" />
        <div className="relative mx-auto max-w-7xl">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-4 py-1.5 text-sm font-medium text-slate-300 backdrop-blur">
              <span className="h-2 w-2 rounded-full bg-green-500" /> Live chat, tickets & AI in one place
            </span>
            <h1 className="mt-6 text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              The place where every <span className="gradient-text">business conversation</span> happens
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-400">
              Zuri Hub unifies live chat, tickets, knowledge base, omnichannel messaging, AI voice, and analytics into one fast, beautiful platform.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <a
                href={site.workspaceUrl}
                target="_blank"
                rel="noreferrer"
                className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-bold text-slate-900 shadow-xl shadow-white/10 transition hover:bg-slate-100"
              >
                Launch Workspace
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
              <Link
                to="/features"
                className="group inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-7 py-3.5 text-base font-semibold text-white backdrop-blur transition hover:border-brand-500 hover:bg-slate-800"
              >
                <Play className="h-4 w-4" /> Explore Features
              </Link>
            </div>
            <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-slate-800 bg-slate-900/40 px-5 py-2 text-sm text-slate-400 backdrop-blur">
              <div className="flex -space-x-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star key={s} className="h-4 w-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span>4.8 / 5 from 250+ teams</span>
            </div>
          </div>

          {/* Abstract dashboard preview */}
          <div className="relative mx-auto mt-16 max-w-5xl">
            <div className="absolute inset-0 -skew-y-3 rounded-3xl bg-gradient-to-r from-brand-600/30 to-indigo-500/30 blur-2xl" />
            <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/80 p-2 shadow-2xl backdrop-blur">
              <div className="flex items-center gap-2 rounded-t-xl bg-slate-950 px-4 py-3">
                <div className="h-3 w-3 rounded-full bg-red-500" />
                <div className="h-3 w-3 rounded-full bg-amber-500" />
                <div className="h-3 w-3 rounded-full bg-green-500" />
                <span className="ml-4 text-xs text-slate-500">workspace.zurihub.app</span>
              </div>
              <div className="grid gap-3 rounded-b-xl bg-slate-900 p-4 sm:grid-cols-3">
                <div className="col-span-2 space-y-3 rounded-xl bg-slate-800/50 p-4">
                  <div className="h-2.5 w-1/3 rounded bg-slate-700" />
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-3 rounded-lg bg-slate-800 p-3">
                        <div className="h-8 w-8 rounded-full bg-slate-700" />
                        <div className="flex-1 space-y-2">
                          <div className="h-2 w-3/4 rounded bg-slate-600" />
                          <div className="h-2 w-1/2 rounded bg-slate-700" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-3 rounded-xl bg-slate-800/50 p-4">
                  <div className="h-2.5 w-2/3 rounded bg-slate-700" />
                  <div className="h-24 rounded-lg bg-slate-800" />
                  <div className="h-24 rounded-lg bg-slate-800" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social proof */}
      <section className="border-b border-slate-100 bg-white py-16" aria-label="Customer trust">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 md:grid-cols-2">
            <div>
              <h2 className="text-3xl font-extrabold text-slate-900 sm:text-4xl">
                250+ customers trust Zuri Hub with the hard stuff
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                From fast-growing SaaS teams to global e-commerce brands, teams run their customer experience on Zuri Hub.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {customerLogos.map((logo) => (
                  <span key={logo} className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700">
                    {logo}
                  </span>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {customerStats.map((stat) => (
                <div key={stat.label} className="rounded-3xl border border-slate-100 bg-slate-50 p-6 text-center transition hover:-translate-y-1 hover:shadow-md">
                  <div className="text-3xl font-extrabold text-brand-700 sm:text-4xl">{stat.value}</div>
                  <div className="mt-1 text-sm font-semibold text-slate-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-24" aria-labelledby="industries-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="industries-heading" className="section-title">Industries we serve</h2>
            <p className="section-subtitle mx-auto">Built for the customer journeys that matter.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {industries.map((industry) => (
              <div
                key={industry.name}
                className="group rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600 transition group-hover:bg-brand-600 group-hover:text-white">
                  <Icon name={industry.icon} className="h-7 w-7" />
                </div>
                <div className="text-sm font-bold text-brand-600">{industry.stat}</div>
                <h3 className="mt-1 text-xl font-bold text-slate-900">{industry.name}</h3>
                <p className="mt-3 text-slate-600">{industry.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="bg-slate-50 py-24" aria-labelledby="products-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="products-heading" className="section-title">Built for modern CX teams</h2>
            <p className="section-subtitle mx-auto">Three products. One platform. No tool-hopping.</p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {products.map((product, index) => (
              <div
                key={product.id}
                className={`group relative overflow-hidden rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl ${index === 1 ? 'md:scale-[1.02] ring-1 ring-brand-200' : ''}`}
              >
                {index === 1 && (
                  <span className="absolute right-6 top-6 rounded-full bg-brand-100 px-3 py-1 text-xs font-bold text-brand-700">
                    Core Platform
                  </span>
                )}
                <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-100 to-brand-50 text-brand-700 shadow-inner transition group-hover:from-brand-600 group-hover:to-brand-700 group-hover:text-white">
                  <Icon name={productIcons[product.id]} className="h-8 w-8" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900">{product.name}</h3>
                <p className="mt-2 font-semibold text-brand-600">{product.tagline}</p>
                <p className="mt-4 text-slate-600">{product.description}</p>
                <ul className="mt-6 space-y-3">
                  {product.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm font-semibold text-slate-700">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex items-center justify-between">
                  <Link
                    to="/product"
                    className="inline-flex items-center gap-1 text-sm font-bold text-brand-600 transition group-hover:gap-2"
                  >
                    {product.cta} <ArrowRight className="h-4 w-4" />
                  </Link>
                  <a
                    href={site.workspaceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-full bg-slate-900 px-4 py-2 text-xs font-bold text-white transition hover:bg-slate-800"
                  >
                    Try free
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Powerful features, delightful UX</h2>
            <p className="section-subtitle mx-auto">Everything your team needs to close conversations faster.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="group rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600 shadow-inner transition group-hover:bg-brand-600 group-hover:text-white">
                  <Icon name={feature.icon} className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{feature.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/features"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-2.5 text-sm font-bold text-slate-700 transition hover:border-brand-300 hover:text-brand-600"
            >
              View All Features <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Channels */}
      <section className="bg-slate-950 py-24 text-white" aria-labelledby="channels-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-16 lg:grid-cols-2">
            <div>
              <h2 id="channels-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">
                All your channels, one <span className="gradient-text">inbox</span>
              </h2>
              <p className="mt-4 text-lg text-slate-400">
                WhatsApp, Instagram, Telegram, X, Messenger, Gmail, live chat, and email — every conversation lands in one place.
              </p>
              <ul className="mt-8 space-y-4">
                {['Reply from a single thread', 'No missed messages', 'Shared team context', 'Built-in analytics'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-slate-300">
                    <Check className="h-5 w-5 text-green-500" /> {item}
                  </li>
                ))}
              </ul>
              <a
                href={site.workspaceUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-bold text-slate-900 shadow-xl shadow-white/10 transition hover:bg-slate-100"
              >
                Launch Workspace <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {channels.map((channel) => (
                <div
                  key={channel.name}
                  className="flex items-center gap-4 rounded-2xl border border-slate-800 bg-slate-900/60 p-4 backdrop-blur transition hover:-translate-y-1 hover:bg-slate-800"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-800 shadow-sm">
                    <BrandIcon name={channel.brand} className="h-7 w-7" />
                  </div>
                  <span className="text-base font-bold text-white">{channel.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Calculator */}
      <section className="bg-slate-50 py-24" aria-labelledby="calculator-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 id="calculator-heading" className="text-3xl font-extrabold text-slate-900 sm:text-4xl">
                {calculator.title}
              </h2>
              <p className="mt-4 text-lg text-slate-600">{calculator.subtitle}</p>
              <div className="mt-10 rounded-3xl bg-slate-900 p-8 text-white shadow-xl">
                <p className="text-sm font-bold text-slate-400 uppercase tracking-wider">Annual savings vs {calculator.competitors[0].name}</p>
                <p className="mt-2 text-5xl font-extrabold text-white sm:text-6xl">
                  {fmt.format((calculator.competitors[0].price - calculator.zuriPrice) * agents * 12)}
                </p>
                <p className="mt-2 text-slate-400">for a team of {agents} agents</p>
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 text-base font-bold text-slate-900 transition hover:bg-slate-100"
                >
                  Start saving with Zuri <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="rounded-3xl border border-slate-100 bg-white p-8 shadow-xl sm:p-10">
              <div className="flex items-center justify-between">
                <label htmlFor="agents" className="text-sm font-bold text-slate-700">
                  Number of agents
                </label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={agents}
                  onChange={(e) => {
                    const v = parseInt(e.target.value);
                    setAgents(Math.max(1, Math.min(100, isNaN(v) ? 1 : v)));
                  }}
                  className="w-20 rounded-xl border border-slate-200 px-3 py-2 text-right font-bold text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
              <input
                id="agents"
                type="range"
                min={1}
                max={100}
                value={agents}
                onChange={(e) => setAgents(parseInt(e.target.value))}
                className="mt-4 w-full accent-brand-600"
              />
              <div className="mt-8 space-y-5">
                {calculator.competitors.map((comp) => {
                  const theirCost = comp.price * agents;
                  const zuriCost = calculator.zuriPrice * agents;
                  const save = theirCost - zuriCost;
                  return (
                    <div key={comp.name}>
                      <div className="flex items-center justify-between text-sm font-semibold">
                        <span className="text-slate-900">{comp.name}</span>
                        <span className="text-slate-500">{fmt.format(theirCost)}/mo</span>
                      </div>
                      <div className="mt-2 h-4 w-full overflow-hidden rounded-full bg-slate-100">
                        <div
                          className={`h-4 rounded-full transition-all duration-300 ${comp.color}`}
                          style={{ width: `${(comp.price / maxPrice) * 100}%` }}
                        />
                      </div>
                      <p className="mt-1 text-xs font-bold text-green-600">Save {fmt.format(save)}/mo vs {comp.name}</p>
                    </div>
                  );
                })}
                <div className="rounded-2xl bg-brand-50 p-5">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-900">Zuri Hub Pro</span>
                    <span className="text-2xl font-extrabold text-brand-700">{fmt.format(calculator.zuriPrice * agents)}/mo</span>
                  </div>
                  <p className="mt-2 text-sm font-semibold text-slate-600">
                    That is {fmt.format(maxPrice * agents)}/mo worth of CX software for {fmt.format(calculator.zuriPrice * agents)}.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Migration */}
      <section className="py-24" aria-labelledby="migration-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h2 id="migration-heading" className="section-title">{migration.title}</h2>
              <p className="section-subtitle mx-auto mt-4 lg:mx-0">{migration.subtitle}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                {migration.from.map((tool) => (
                  <span key={tool} className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm">
                    {tool}
                  </span>
                ))}
              </div>
            </div>
            <div className="rounded-3xl bg-slate-900 p-8 text-white shadow-xl">
              <h3 className="text-xl font-bold">Why teams switch</h3>
              <ul className="mt-6 space-y-4">
                {migration.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-400" />
                    <span className="text-slate-300">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="bg-slate-50 py-24" aria-labelledby="compare-heading">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 id="compare-heading" className="section-title">{comparison.title}</h2>
          </div>
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-left">
                    <th className="px-6 py-4 font-bold text-slate-700">Feature</th>
                    <th className="px-6 py-4 text-center font-bold text-brand-700">Zuri Hub</th>
                    <th className="px-6 py-4 text-center font-bold text-slate-700">Intercom</th>
                    <th className="px-6 py-4 text-center font-bold text-slate-700">Zendesk</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.rows.map((row) => (
                    <tr key={row.feature} className="border-b border-slate-100 last:border-0">
                      <td className="px-6 py-4 font-medium text-slate-900">{row.feature}</td>
                      <td className="px-6 py-4 text-center">{compareCell(row.zurihub, true)}</td>
                      <td className="px-6 py-4 text-center">
                        {typeof row.intercom === 'string' && ['partial', 'add-on'].includes(row.intercom) ? badge(row.intercom) : compareCell(row.intercom)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {typeof row.zendesk === 'string' && ['partial', 'add-on'].includes(row.zendesk) ? badge(row.zendesk) : compareCell(row.zendesk)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-950 py-24 text-white" aria-label="Get started today">
        <div className="relative mx-auto max-w-5xl overflow-hidden rounded-3xl bg-gradient-to-br from-brand-700 to-indigo-900 px-6 py-16 text-center shadow-2xl sm:px-12 lg:px-16">
          <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-12 -left-12 h-40 w-40 rounded-full bg-brand-400/20 blur-3xl" />
          <div className="relative">
            <h2 className="text-3xl font-bold sm:text-4xl">Ready to transform your customer conversations?</h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-brand-100">
              Start free, explore the product, and scale as your team grows.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <a
                href={site.workspaceUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 shadow-lg transition hover:bg-slate-100"
              >
                Launch Workspace <ArrowUpRight className="h-4 w-4" />
              </a>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-8 py-3.5 text-base font-semibold text-white backdrop-blur transition hover:bg-white/20"
              >
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="faq-heading" className="mb-12 text-center text-3xl font-bold text-slate-900 sm:text-4xl">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq) => (
              <details
                key={faq.question}
                className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
                open
              >
                <summary className="flex cursor-pointer list-none items-center justify-between text-lg font-bold text-slate-900">
                  {faq.question}
                  <ChevronRight className="h-5 w-5 text-slate-400 transition group-open:rotate-90" />
                </summary>
                <p className="mt-4 leading-relaxed text-slate-600">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
